<template>
    <div class="border-t-2 border-blue-600 mt-2 md:mt-4">
        <div class="tan-control-box">
            <p class="select-title mb-1 print:hidden">Select:</p>

            <option-selector-2
                v-model="symbol"
                :initial-symbol="underlyingSymbolInfo"
                :groups="allContracts"
                >
            </option-selector-2>

            <div class="inline-block mb-3 print:hidden">
                <div @click="changeSymbol" class="btn btn-sm btn-yellow">Show Option History</div>
            </div>
        </div>
    </div>

    <div v-if="showHistory">
        <option-info-block
            :loading="loading.quotes"
            :mini-chart="miniChart"
            :quotes="quotes">
        </option-info-block>

        <div class="flex">
            <span class="text-lg font-bold">{{ tableName }}</span>
            <div v-if="!loading.dataTable" class="inline-block justify-end mb-2 ml-auto print:hidden">
                <dt-export :file-name="fileName"></dt-export>
            </div>
        </div>

        <data-table
            :flip-charts-button="false"
            :default-columns="columns"
            :default-request="request">
        </data-table>
    </div>

    <div v-if="!showHistory">
        <alert-box>
            <template #body>
                <div class="color-inherit">Please select a strike price.</div>
            </template>
        </alert-box>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue';

const props = defineProps({
    allContracts: {
        type: Object,
        required: true
    },
    initialSymbol: String,
    underlyingSymbolInfo: Object,
    modelValue: {
        type: [String, Object],
        default: null, // You can set a default value if necessary
    },
});

const symbol = ref(props.initialSymbol);
const loading = ref({
    quotes: false,
    dataTable: false,
    history: false
});
const showHistory = ref(false);
const miniChart = ref({});
const quotes = ref(null);
const request = ref({});
const tableName = ref(null);
const underlyingSymbolInfo = ref(props.underlyingSymbolInfo);
const columns = ref([
    { label: 'Date', field: 'tradeTime' },
    { label: 'Open', field: 'openPrice' },
    { label: 'High', field: 'highPrice' },
    { label: 'Low', field: 'lowPrice' },
    { label: 'Close', field: 'lastPrice' },
    { label: 'Volume', field: 'volume' },
    { label: 'Open Int.', field: 'openInterest' },
    { label: 'Premium ($)', field: 'premium' },
]);

const fileName = computed(() => {
    if (quotes.value) {
        const strike = props.underlyingSymbolInfo.symbol.split('|')[1];
        return `${quotes.value.symbolName} ${quotes.value.contractMonthYear} ${strike} History`;
    }
});

const eventBus = inject('eventBus');

// function formatOptionsForSelect(data) {
//     return data.map((row) => {
//         const isDefault = symbol.value === row.longSymbol;
//         return {
//             label: row.strikePrice,
//             value: row.longSymbol,
//             default: isDefault
//         };
//     });
// }

function getHistory() {
    showHistory.value = true;
    loading.value.history = true;
    loading.value.dataTable = true;

    getQuotes();
    getTableName();

    request.value = dataTableRequest();
}

function updateMiniChart() {
    miniChart.value = {
        symbol: underlyingSymbolInfo.value.underlying,
        title: `${quotes.value.symbolName} ${quotes.value.contractMonthYear} Daily Chart`
    };
}

function getTableName() {
    const route = window.route('api.quotes');

    const params = {
        symbol: symbol.value,
        fields: 'symbolName'
    };

    axios.post(route, params)
        .then((response) => {
            tableName.value = `${response.data.data[0].symbolName} History`;
        }).catch(error => { });
}

function getQuotes() {
    const route = window.route('api.quotes');
    loading.value.quotes = true;

    const params = {
        symbol: underlyingSymbolInfo.value.underlying,
        fields: 'symbolName,daysToExpiration,optionsExpiration,lastPrice,pointValue,impliedVolatility,contractMonthYear,symbol'
    };

    axios.post(route, params)
        .then((response) => {
            quotes.value = response.data.data[0];
            updateMiniChart();
        }).catch((error) => {
            console.error(error);
        }).then(() => {
            loading.value.quotes = false;
        });
}

function changeSymbol() {
    const route = window.route('options.history', { symbol: symbol.value });
    window.location = route;
}

function dataTableRequest() {
    return {
        route: `${window.route('api.quotes')}`,
        method: 'historical',
        limit: 250,
        symbol: symbol.value,
        type: 'eod',
        fields: 'tradeTime.format(m/d/Y),openPrice,highPrice,lowPrice,lastPrice,volume,openInterest',
        orderBy: 'tradeTime',
        orderDir: 'desc',
        after: 'calculatePremium'
    };
}

onMounted(() => {
    eventBus.on('data-loaded', () => {
        loading.value.dataTable = false;
    });

    if (props.initialSymbol && props.underlyingSymbolInfo) {
        getHistory();
    }
});
</script>
